import type { API } from 'chessboard/api/config/types';
import type { Keys } from 'chessboard/plugins/keys/config/types';
import { ArrowKeys } from 'plugins/types';
import type { Core } from 'chessboard/core/config/types';
import type { Plugins } from 'chessboard/core/plugins/config/types';

/**
 * Arrow keys plugin which allows going through moves via keys.
 */
export function createArrowKeysPlugin(customOverrides?: ArrowKeys.ArrowEvents): Plugins.Model {
  let ctx: Core.Context;

  const arrowKeys = {
    [ArrowKeys.Arrow.Up]: 'selectLineStart',
    [ArrowKeys.Arrow.NumUp]: 'selectLineStart',
    [ArrowKeys.Arrow.Down]: 'selectLineEnd',
    [ArrowKeys.Arrow.NumDown]: 'selectLineEnd',
    [ArrowKeys.Arrow.Left]: 'moveBackward',
    [ArrowKeys.Arrow.NumLeft]: 'moveBackward',
    [ArrowKeys.Arrow.Right]: 'moveForward',
    [ArrowKeys.Arrow.NumRight]: 'moveForward',
  };

  return {
    api: (): ArrowKeys.API => ({
      setKeys,
      resetKeys,
    }),
    create,
    destroy,
    name: ArrowKeys.Plugin.Name,
  };

  /**
   * Set keydown callbacks at create
   */
  function create(context: Core.Context) {
    ctx = context;

    setKeys(customOverrides);
  }

  /**
   * Set arrow-keys.
   */
  function setKeys(arrowEvents?: ArrowKeys.ArrowEvents): void {
    const overrides = getOverrides(arrowEvents);

    ctx.api.keys.setOverrides(overrides);
  }

  /**
   * Reset arrow-keys.
   */
  function resetKeys(): void {
    const overrides = getOverrides();

    ctx.api.keys.setOverrides(overrides);
  }

  /**
   * Get callbacks helper function
   */
  function getOverrides(arrowEvents: ArrowKeys.ArrowEvents = {}): Keys.Override[] {
    return Object.entries(arrowKeys).map(([key, method]) => ({
      key,
      callback: (api: API.ExtendedAPI) =>
        api.getOptions().disableUserNavigation ? () => {} : arrowEvents[key] ?? api[method],
    }));
  }

  /**
   * Destroy the analysis overlay plugin.
   */
  function destroy() {
    ctx.api.keys.removeOverrides(Object.keys(arrowKeys).map((key) => ({ key })));
  }
}
